import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Coffee, Sun, Salad, UtensilsCrossed, Pencil } from 'lucide-react';
import { recipes } from '../data/recipes';

// 7 systems × 3 days = 21-day journey
const SYSTEMS = [
  { id: 'root', name: 'The Root', tag: 'Red / Root', band: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-400', text: 'text-red-700',
    tip: "Lots of amazing Root recipes to choose from — these are just ideas. To make my week easy, I'll roast a big batch of root veg (beets, carrots, parsnips, sweet potato — whatever tickles me), with or without protein. They become quick meals, snacks and lunch leftovers, add interest to salads, and make lovely cool summer foods that are still cooked and easy on digestion — no standing over a hot stove. I'll pair them with a quick protein one evening, then add the leftovers to lunch alongside an elixir, juice or smoothie in the AM, and my week gets so much easier. Tip: add powdered Vitamin C (a capsule works) to a smoothie or juice to keep it fresher if I'm making it for two days. Magic Chili uses a lot of the same veg, so it makes for an easy shopping week — and it freezes beautifully for variety and ease later." },
  { id: 'flow', name: 'The Flow', tag: 'Orange / Flow', band: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-400', text: 'text-orange-700' },
  { id: 'fire', name: 'The Fire', tag: 'Yellow / Fire', band: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-400', text: 'text-amber-700' },
  { id: 'love', name: 'The Love', tag: 'Green / Love', band: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-400', text: 'text-emerald-700' },
  { id: 'truth', name: 'The Truth', tag: 'Blue / Truth', band: 'bg-sky-50', border: 'border-sky-200', dot: 'bg-sky-400', text: 'text-sky-700' },
  { id: 'insight', name: 'The Insight', tag: 'Indigo / Insight', band: 'bg-indigo-50', border: 'border-indigo-200', dot: 'bg-indigo-400', text: 'text-indigo-700' },
  { id: 'spirit', name: 'The Spirit', tag: 'Violet / Spirit', band: 'bg-violet-50', border: 'border-violet-200', dot: 'bg-violet-400', text: 'text-violet-700' },
];

const MEALS = [
  { key: 'drink', label: 'Drink', cats: ['teas'], Icon: Coffee },
  { key: 'breakfast', label: 'Breakfast', cats: ['smoothies'], Icon: Sun },
  { key: 'lunch', label: 'Lunch', cats: ['salads'], Icon: Salad },
  { key: 'dinner', label: 'Dinner', cats: ['mains', 'soups'], Icon: UtensilsCrossed },
];

const hasTag = (r, tag) =>
  r.tag === tag || (Array.isArray(r.tags) && (r.tags.includes(tag) || r.tags.includes('Rainbow / All Systems')));

// Build recipe pools per system + meal
const buildPools = () => {
  const pools = {};
  SYSTEMS.forEach((s) => {
    pools[s.id] = {};
    MEALS.forEach((m) => {
      pools[s.id][m.key] = recipes.filter((r) => hasTag(r, s.tag) && m.cats.includes(r.category));
    });
  });
  return pools;
};

export const MealMap21 = () => {
  const pools = buildPools();
  // offsets keyed by `${day}-${mealKey}` for substitute cycling
  const [offsets, setOffsets] = useState({});
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gw_meal_notes')) || {}; } catch { return {}; }
  });
  const [openNotes, setOpenNotes] = useState(() => new Set());
  const [sysNotes, setSysNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gw_meal_sysnotes')) || {}; } catch { return {}; }
  });
  const [openSysNotes, setOpenSysNotes] = useState(() => new Set());

  const setSysNote = (id, val) => {
    setSysNotes((prev) => {
      const next = { ...prev, [id]: val };
      localStorage.setItem('gw_meal_sysnotes', JSON.stringify(next));
      return next;
    });
  };
  const toggleSysNote = (id) => {
    setOpenSysNotes((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const setNote = (key, val) => {
    setNotes((prev) => {
      const next = { ...prev, [key]: val };
      localStorage.setItem('gw_meal_notes', JSON.stringify(next));
      return next;
    });
  };
  const toggleNote = (key) => {
    setOpenNotes((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const substitute = (day, mealKey, poolLen) => {
    if (poolLen <= 1) return;
    setOffsets((prev) => ({ ...prev, [`${day}-${mealKey}`]: (prev[`${day}-${mealKey}`] || 0) + 1 }));
  };

  return (
    <div className="space-y-8" data-testid="meal-map-21">
      <p className="font-body text-sm text-stone-600">
        Your 21-day color journey — 3 days per system. Each day has a drink, breakfast, lunch, and dinner idea pulled
        from that color's recipes. Tap <RefreshCw size={12} className="inline -mt-0.5" /> <span className="font-medium">Swap</span> to
        cycle through other options for that meal, tap any recipe to open it, or tap <Pencil size={12} className="inline -mt-0.5" /> to type your own idea (it saves automatically).
      </p>

      {SYSTEMS.map((sys, sysIdx) => (
        <div key={sys.id} className={`rounded-2xl border ${sys.border} overflow-hidden`} data-testid={`meal-map-${sys.id}`}>
          <div className={`${sys.band} px-5 py-3 flex items-center gap-2`}>
            <span className={`w-3 h-3 rounded-full ${sys.dot}`} />
            <h4 className={`font-heading text-lg ${sys.text}`}>{sys.name}</h4>
            <span className="text-xs font-body text-stone-500 ml-auto mr-2">Days {sysIdx * 3 + 1}–{sysIdx * 3 + 3}</span>
            <button
              onClick={() => toggleSysNote(sys.id)}
              className={`transition-colors ${openSysNotes.has(sys.id) || sysNotes[sys.id] ? 'text-terracotta' : 'text-stone-400 hover:text-terracotta'}`}
              title="Add a quick note for these 3 days"
              data-testid={`sysnote-toggle-${sys.id}`}
            >
              <Pencil size={14} />
            </button>
          </div>
          <div className={`${sys.band} px-5 pb-3 border-b ${sys.border} space-y-2`}>
            {sys.tip && (
              <p className="font-body text-sm text-stone-600 leading-relaxed">{sys.tip}</p>
            )}
            {(openSysNotes.has(sys.id) || sysNotes[sys.id]) && (
              <textarea
                value={sysNotes[sys.id] || ''}
                onChange={(e) => setSysNote(sys.id, e.target.value)}
                placeholder="Add your own quick notes for these 3 days…"
                rows={2}
                className="w-full text-sm font-body text-stone-700 bg-white/70 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta resize-none placeholder:text-stone-400"
                data-testid={`sysnote-input-${sys.id}`}
              />
            )}
          </div>

          <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-stone-100">
            {[0, 1, 2].map((p) => {
              const day = sysIdx * 3 + p + 1;
              return (
                <div key={day} className={`${sys.band} bg-opacity-30 p-4`} data-testid={`meal-day-${day}`}>
                  <p className={`font-heading text-base ${sys.text} mb-3`}>Day {day}</p>
                  <div className="space-y-3">
                    {MEALS.map((meal) => {
                      const pool = pools[sys.id][meal.key];
                      const offset = offsets[`${day}-${meal.key}`] || 0;
                      const recipe = pool.length ? pool[(p + offset) % pool.length] : null;
                      const Icon = meal.Icon;
                      const noteKey = `${day}-${meal.key}`;
                      const noteVal = notes[noteKey] || '';
                      const noteOpen = openNotes.has(noteKey) || noteVal;
                      return (
                        <div key={meal.key}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Icon size={12} className="text-stone-400" />
                            <span className="text-[11px] font-body uppercase tracking-wider text-stone-400">{meal.label}</span>
                            <div className="ml-auto flex items-center gap-1.5">
                              {pool.length > 1 && (
                                <button
                                  onClick={() => substitute(day, meal.key, pool.length)}
                                  className="text-stone-400 hover:text-terracotta transition-colors"
                                  title="Swap for another option"
                                  data-testid={`swap-${day}-${meal.key}`}
                                >
                                  <RefreshCw size={12} />
                                </button>
                              )}
                              <button
                                onClick={() => toggleNote(noteKey)}
                                className={`transition-colors ${noteOpen ? 'text-terracotta' : 'text-stone-400 hover:text-terracotta'}`}
                                title="Add your own idea"
                                data-testid={`note-toggle-${day}-${meal.key}`}
                              >
                                <Pencil size={12} />
                              </button>
                            </div>
                          </div>
                          {recipe ? (
                            <Link
                              to={`/resources/recipes?resource=${recipe.id}`}
                              className="font-body text-sm text-forest hover:text-terracotta transition-colors leading-snug block"
                            >
                              {recipe.title}
                            </Link>
                          ) : (
                            <span className="font-body text-sm text-stone-300 italic">Coming soon</span>
                          )}
                          {noteOpen && (
                            <textarea
                              value={noteVal}
                              onChange={(e) => setNote(noteKey, e.target.value)}
                              placeholder="Type your own idea…"
                              rows={2}
                              className="mt-1.5 w-full text-sm font-body text-stone-700 bg-white/70 border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-terracotta resize-none placeholder:text-stone-300"
                              data-testid={`note-input-${day}-${meal.key}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MealMap21;
